/**
 * What the table blocks share: the pagination window, the sentiment badge
 * map, and the status-aware empty line. Ported from the prototype's
 * `views/tables/pageWindow.ts` and `sentimentBadge.ts`.
 */
import type { BadgeType } from '../../../atoms/Badge/Badge';
import type { WidgetStatus } from '../../widgets/shared/cardShell';

/**
 * The page numbers a `Pagination` footer shows: a sliding window of five
 * around the current page — the frame draws exactly five number buttons, kept
 * centred where it can be and pinned to either end where it cannot.
 */
export const PAGE_WINDOW = 5;

export function pageWindow(page: number, pageCount: number, size: number = PAGE_WINDOW): number[] {
  const count = Math.min(size, pageCount);
  const start = Math.min(Math.max(1, page - Math.floor(count / 2)), pageCount - count + 1);
  return Array.from({ length: count }, (_, i) => start + i);
}

/**
 * A sentiment value → the `Badge` type that colours it. `gray` is the fallback
 * at every call site, not a fourth entry: a value outside the three is not a
 * sentiment.
 */
export const SENTIMENT_BADGE: Readonly<Record<string, BadgeType>> = {
  positive: 'positive',
  neutral: 'neutral',
  negative: 'negative',
};

/** The shared whole-number format every table cell uses. */
export const wholeFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * A table's empty line, status-aware. `Table` has no `State=Loading` variant
 * (the prototype's tables re-derived instantly), so while the data is on its
 * way the body says so in the empty band — the same thing Community's ranking
 * tables did while the prototype dashboard settled. `error` / `notConnected`
 * have no frame yet (docs/design/open-questions.md #2).
 */
export function tableEmptyLabel(status: WidgetStatus, readyLabel: string): string {
  switch (status) {
    case 'loading':
      return 'Loading…';
    case 'error':
      return 'Couldn’t load this table.';
    case 'notConnected':
      return 'Live data for this table is not connected yet.';
    default:
      return readyLabel;
  }
}
